<template>
  <UiModal
    v-if="newUiEnabled"
    :model-value="isVisible"
    :title="$t('Enter password to continue')"
    :icon="UserCheck"
    size="s"
    :close-on-overlay="false"
    initial-focus="#existing-account-password"
    @update:model-value="onModelValueUpdate"
  >
    <form class="auth-card__form" @submit.prevent="onSubmit">
      <UiAlert>
        {{
          $t(
            "If an account already exists for '{emailAddress}', enter its password below to link this catering to it.",
            { emailAddress: emailAddress ?? "" },
          )
        }}
      </UiAlert>

      <AuthPasswordInput
        id="existing-account-password"
        v-model="password"
        :label="$t('Password for the existing account')"
        :icon="Lock"
        autocomplete="current-password"
        :placeholder="$t('Enter your password...')"
        :disabled="isSubmitting"
        required
      />

      <div>
        <button
          type="button"
          class="auth-link auth-link--button"
          :disabled="isSubmitting || isSendingPasswordReset"
          @click="onForgotPasswordClick"
        >
          {{ $t("I don't remember the password") }}
        </button>
      </div>

      <UiAlert v-if="passwordResetRequested">
        {{
          $t(
            "If an account exists for '{emailAddress}', we sent a password reset link to it. This form stays open - come back once you have reset the password.",
            { emailAddress: emailAddress ?? "" },
          )
        }}
      </UiAlert>

      <UiAlert v-if="errorMessage" tone="danger">{{ errorMessage }}</UiAlert>
    </form>

    <template #footer>
      <UiButton variant="secondary" :icon="X" :disabled="isSubmitting" @click="onHide">
        {{ $t("Cancel") }}
      </UiButton>

      <UiButton
        variant="primary"
        :icon="Check"
        :loading="isSubmitting"
        :disabled="!password && !isSubmitting"
        @click="onSubmit"
      >
        {{ $t("Confirm") }}
      </UiButton>
    </template>
  </UiModal>

  <b-modal
    v-else
    id="existing-account-password-modal"
    ref="existingAccountPasswordModal"
    size="md"
    header-bg-variant="light"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    @hide="onHide"
  >
    <template #title>
      <UiIcon fa="fa fa-user-check" />
      {{ $t("Enter password to continue") }}
    </template>

    <b-alert :model-value="true" variant="warning">
      {{
        $t(
          "If an account already exists for '{emailAddress}', enter its password below to link this catering to it.",
          { emailAddress: emailAddress ?? "" },
        )
      }}
    </b-alert>

    <b-form @submit.prevent="onSubmit">
      <b-form-group>
        <label>{{ $t("Password for the existing account") }}</label>

        <div class="custom-password-input-container">
          <b-input-group>
            <div class="input-group-text">
              <UiIcon fa="fa fa-lock" />
            </div>

            <input
              v-model="password"
              class="form-control border-right-radius-1"
              :disabled="isSubmitting"
              :placeholder="$t('Password')"
              required
              autofocus
              :type="isPasswordVisible ? 'text' : 'password'"
              @keyup.enter="onSubmit"
            />
          </b-input-group>

          <UiIcon
            :fa="
              isPasswordVisible
                ? 'fa fa-eye-slash custom-password-input-button'
                : 'fa fa-eye custom-password-input-button'
            "
            :title="isPasswordVisible ? $t('Hide password') : $t('Show password')"
            @click="isPasswordVisible = !isPasswordVisible"
          />
        </div>
      </b-form-group>

      <p class="mb-0">
        <button
          type="button"
          class="btn btn-link p-0"
          :disabled="isSubmitting || isSendingPasswordReset"
          @click="onForgotPasswordClick"
        >
          {{ $t("I don't remember the password") }}
        </button>
      </p>

      <b-alert v-if="passwordResetRequested" :model-value="true" variant="info" class="mt-3">
        {{
          $t(
            "If an account exists for '{emailAddress}', we sent a password reset link to it. This form stays open - come back once you have reset the password.",
            { emailAddress: emailAddress ?? "" },
          )
        }}
      </b-alert>

      <b-alert v-if="errorMessage" :model-value="true" variant="danger" class="mt-3">
        {{ errorMessage }}
      </b-alert>
    </b-form>

    <template #footer>
      <div class="d-flex gap-1 justify-content-between w-100">
        <button
          type="button"
          class="button-with-outline py-2 px-3"
          :disabled="isSubmitting"
          @click="onHide"
        >
          <UiIcon fa="fa fa-times" />
          {{ $t("Cancel") }}
        </button>

        <button
          type="button"
          class="button-with-outline py-2 px-3"
          :disabled="isSubmitting || !password"
          @click="onSubmit"
        >
          <b-spinner v-if="isSubmitting" style="height: 12px; width: 12px" />
          <UiIcon v-else fa="fa fa-check" />
          {{ $t("Confirm") }}
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script setup lang="ts">
import { ref, watch, useTemplateRef } from "vue";
import { BModal } from "bootstrap-vue-next";
import { usePostApiAuthPasswordReset } from "@generated/auth/query/auth.ts";
import { showApiErrorNotification } from "@/lib/axios-interceptor";
import { Check, Lock, UserCheck, X } from "lucide-vue-next";
import AuthPasswordInput from "./newUi/AuthPasswordInput.vue";
import { useNewUiEnabled } from "@/components/newUi/core/useNewUiEnabled";

const props = defineProps<{
  isVisible: boolean;
  emailAddress: string | null;
  isSubmitting: boolean;
  errorMessage: string | null;
}>();

const emit = defineEmits<{
  (e: "submit", password: string): void;
  (e: "hide"): void;
}>();

const newUiEnabled = useNewUiEnabled();
const existingAccountPasswordModal = useTemplateRef<typeof BModal>("existingAccountPasswordModal");
const password = ref("");
const isPasswordVisible = ref(false);
const passwordResetRequested = ref(false);
// suppressErrorNotification: this component shows its own error below
const { mutateAsync: requestPasswordReset, isPending: isSendingPasswordReset } =
  usePostApiAuthPasswordReset({ request: { suppressErrorNotification: true } });

watch(
  () => props.isVisible,
  (newValue) => {
    if (newValue) {
      password.value = "";
      isPasswordVisible.value = false;
      passwordResetRequested.value = false;
      existingAccountPasswordModal.value?.show();
    } else {
      existingAccountPasswordModal.value?.hide();
    }
  },
);

const onSubmit = () => {
  if (!password.value || props.isSubmitting) {
    return;
  }

  emit("submit", password.value);
};

const onHide = () => {
  if (props.isSubmitting) {
    return;
  }

  emit("hide");
};

const onModelValueUpdate = (isOpen: boolean): void => {
  if (!isOpen) {
    onHide();
  }
};

const onForgotPasswordClick = async () => {
  if (!props.emailAddress) {
    return;
  }

  try {
    // stays open on purpose - the registration form behind it must not be lost
    await requestPasswordReset({ data: { emailAddress: props.emailAddress } });

    passwordResetRequested.value = true;
  } catch (error) {
    await showApiErrorNotification(error);
  }
};
</script>
